import { useEffect, useRef, useState } from "react";
import type { RemoteCursor } from "./setupPresence.ts";

export function RemoteCursors({ cursors }: { cursors: RemoteCursor[] }) {
  return (
    <div
      data-loc="phosphor-blog/scripts/world-state-demo/RemoteCursors.tsx:7:7" data-name="div"
      style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 9999 }}
    >
      {cursors.map((cursor) => (
        <Cursor key={cursor.userId} cursor={cursor} />
      ))}
    </div>
  );
}

function Cursor({ cursor }: { cursor: RemoteCursor }) {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [smoothPosition, setSmoothPosition] = useState<{ x: number; y: number } | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const velocityRef = useRef({ vx: 0, vy: 0 });

  useEffect(() => {
    let anchorElement: Element | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let intersectionObserver: IntersectionObserver | null = null;
    let mutationObserver: MutationObserver | null = null;

    const updatePosition = (rect: DOMRectReadOnly | DOMRect) => {
      // Clamp relative positions in case they're outside bounds
      const relX = Math.max(0, Math.min(1, cursor.relX));
      const relY = Math.max(0, Math.min(1, cursor.relY));

      const x = rect.left + relX * rect.width;
      const y = rect.top + relY * rect.height;
      setPosition({ x, y });
    };

    const updateFromElement = () => {
      if (!anchorElement || !anchorElement.isConnected) {
        setPosition(null);
        return;
      }
      const rect = anchorElement.getBoundingClientRect();
      updatePosition(rect);
    };

    const resolveAnchor = () => {
      anchorElement = document.querySelector(`[data-presence-anchor="${cursor.anchorId}"]`);

      if (!anchorElement) {
        setPosition(null);
        return false;
      }

      updateFromElement();

      // Use ResizeObserver for size changes
      resizeObserver = new ResizeObserver(() => {
        updateFromElement();
      });
      resizeObserver.observe(anchorElement);

      // Use IntersectionObserver for position changes (scroll, layout shifts)
      intersectionObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === anchorElement) {
            updatePosition(entry.boundingClientRect);
          }
        }
      });
      intersectionObserver.observe(anchorElement);

      return true;
    };

    // Try to resolve anchor immediately
    if (!resolveAnchor()) {
      // If anchor not found, watch for DOM changes
      mutationObserver = new MutationObserver(() => {
        if (resolveAnchor()) {
          mutationObserver?.disconnect();
          mutationObserver = null;
        }
      });
      mutationObserver.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["data-presence-anchor"],
      });
    }

    return () => {
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [cursor.anchorId, cursor.relX, cursor.relY]);

  // Spring animation effect
  useEffect(() => {
    if (!position) {
      setSmoothPosition(null);
      return;
    }

    // Initialize smooth position on first render
    if (!smoothPosition) {
      setSmoothPosition(position);
      return;
    }

    // Spring physics parameters
    const stiffness = 0.05; // Lower = slower, smoother tracking
    const damping = 0.7; // Lower = more fluid movement

    let currentX = smoothPosition.x;
    let currentY = smoothPosition.y;
    let currentVx = velocityRef.current.vx;
    let currentVy = velocityRef.current.vy;

    const animate = () => {
      // Calculate spring force
      const dx = position.x - currentX;
      const dy = position.y - currentY;

      // Update velocity with spring force and damping
      currentVx += dx * stiffness;
      currentVy += dy * stiffness;
      currentVx *= damping;
      currentVy *= damping;

      // Update position
      currentX += currentVx;
      currentY += currentVy;

      // Store velocity
      velocityRef.current = { vx: currentVx, vy: currentVy };

      setSmoothPosition({ x: currentX, y: currentY });

      // Continue animation if not settled
      const settled =
        Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 && Math.abs(currentVx) < 0.5 && Math.abs(currentVy) < 0.5;

      if (!settled) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    // Cancel any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [position, smoothPosition]);

  // Don't render if anchor not found
  if (!smoothPosition) {
    return null;
  }

  return (
    <div
      data-loc="phosphor-blog/scripts/world-state-demo/RemoteCursors.tsx:173:7" data-name="div"
      style={{
        position: "absolute",
        left: smoothPosition.x,
        top: smoothPosition.y,
        transform: "translate(-2px, -2px)",
        pointerEvents: "none",
      }}
    >
      {/* Cursor SVG */}
      <svg data-loc="phosphor-blog/scripts/world-state-demo/RemoteCursors.tsx:183:12" data-name="svg" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          data-loc="phosphor-blog/scripts/world-state-demo/RemoteCursors.tsx:185:11" data-name="path"
          d="M5.65376 12.3673L4.46887 4.40742L12.5423 8.76209L9.05501 11.4119L12.5423 15.6309L11.1937 16.9604L7.70642 12.7414L5.65376 12.3673Z"
          fill="hsl(220, 90%, 56%)"
          stroke="white"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>

      {/* User name label */}
      {cursor.name && (
        <div
          data-loc="phosphor-blog/scripts/world-state-demo/RemoteCursors.tsx:197:11" data-name="div"
          style={{
            position: "absolute",
            left: 20,
            top: 0,
            backgroundColor: "hsl(220, 90%, 56%)",
            color: "white",
            padding: "2px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            fontWeight: 500,
            whiteSpace: "nowrap",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          {cursor.name}
        </div>
      )}
    </div>
  );
}
